
import { Room, RoomType, Connection, CorridorSegment, GeneratorConfig, EncounterType, DungeonTheme } from '../types';

export class PRNG {
  private seed: number;
  constructor(seedStr: string) {
    let hash = 0;
    for (let i = 0; i < seedStr.length; i++) {
      hash = (hash << 5) - hash + seedStr.charCodeAt(i);
      hash |= 0;
    }
    this.seed = Math.abs(hash);
  }
  next() {
    this.seed = (this.seed * 1664525 + 1013904223) % 4294967296;
    return this.seed / 4294967296;
  }
  range(min: number, max: number) {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }
  pick<T>(arr: T[]): T {
    return arr[Math.floor(this.next() * arr.length)];
  }
}

export const generateProceduralDungeon = (config: GeneratorConfig, theme: DungeonTheme) => {
  const seed = Math.random().toString(36);
  const rng = new PRNG(seed);
  const rooms: Room[] = [];
  const connections: Connection[] = [];

  const TILE_SPACING_X = 14;
  const TILE_SPACING_Y = 10;
  const layers = Math.max(3, Math.floor(config.complexity / 2));

  // Tabelas de Geração
  const MONSTERS = {
    LOW: ['Ratos Gigantes', 'Bandidos', 'Aranhas', 'Esqueletos', 'Cultista', 'Lobo', 'Goblin'],
    MID: ['Gárgula', 'Necromante', 'Cavaleiro Negro', 'Abominação', 'Vampiro', 'Lich'],
    BOSS: ['Dragão Ósseo', 'Lorde das Sombras', 'Beholder', 'Rei Lich', 'Hidra']
  };

  const LOOT = ['Ouro (50po)', 'Jóia (Rubi)', 'Poção de Cura', 'Pergaminho Antigo', 'Armadura Velha', 'Chave Enferrujada', 'Grimório', 'Cristal de Mana'];

  const DESCRIPTIONS = {
    BATTLE: ['O cheio de sangue é forte.', 'Ossos cobrem o chão.', 'Gritos ecoam ao longe.', 'Sombras se movem nas paredes.'],
    TREASURE: ['Um baú brilha no canto.', 'Pilhas de ouro antigo.', 'Um altar esquecido guarda algo.'],
    EMPTY: ['Poeira e silêncio.', 'Apenas ecos.', 'Um local de breve descanso.', 'Nada além de teias de aranha.']
  };

  const startRoom: Room = {
    id: 'room-start',
    type: 'START',
    gridX: 0,
    gridY: 0,
    label: 'Old Road',
    revealed: true,
    enemies: [],
    description: 'A entrada da masmorra. O vento uiva lá fora.'
  };
  rooms.push(startRoom);

  let prevLayerRooms: Room[] = [startRoom];

  for (let l = 1; l <= layers; l++) {
    const currentLayerRooms: Room[] = [];
    const roomsInLayer = l === layers ? 1 : rng.range(1, Math.min(3, config.branching + 1));

    for (let r = 0; r < roomsInLayer; r++) {
      const isBoss = l === layers;
      const type: RoomType = isBoss ? 'BOSS' : rng.pick(['BATTLE', 'BATTLE', 'CURIO', 'TREASURE', 'EMPTY', 'EMPTY', 'CAMP']);

      let enemies: string[] = [];
      let description = '';

      if (isBoss) {
        enemies = [rng.pick(MONSTERS.BOSS)];
        description = 'Uma presença terrível domina esta câmara.';
      } else if (type === 'BATTLE') {
        // 1 a 3 inimigos
        const count = rng.range(1, 3);
        for (let i = 0; i < count; i++) enemies.push(rng.pick(MONSTERS.LOW));
        description = rng.pick(DESCRIPTIONS.BATTLE);
      } else if (type === 'TREASURE') {
        description = `${rng.pick(DESCRIPTIONS.TREASURE)} Contém: ${rng.pick(LOOT)}.`;
      } else {
        description = rng.pick(DESCRIPTIONS.EMPTY || ['Vazio.']);
      }

      const newRoom: Room = {
        id: `room-${l}-${r}`,
        type,
        gridX: l * TILE_SPACING_X,
        gridY: Math.round((r - (roomsInLayer - 1) / 2) * TILE_SPACING_Y),
        label: isBoss ? 'The Horror' : `Chamber ${l}-${r}`,
        revealed: false,
        enemies,
        description
      };
      currentLayerRooms.push(newRoom);
      rooms.push(newRoom);
    }

    currentLayerRooms.forEach((curr, idx) => {
      const parent = prevLayerRooms[idx % prevLayerRooms.length];
      connections.push(createConnection(parent, curr, rng, rooms));
    });

    prevLayerRooms = currentLayerRooms;
  }

  return { rooms, connections };
};

export function createConnection(from: Room, to: Room, rng: any, allRooms: Room[]): Connection {
  const segments: CorridorSegment[] = [];

  for (let x = Math.min(from.gridX, to.gridX); x <= Math.max(from.gridX, to.gridX); x++) {
    segments.push({
      id: `seg-h-${from.id}-${to.id}-${x}`,
      gridX: x,
      gridY: from.gridY,
      encounter: rng.next() > 0.9 ? 'BATTLE' : 'NONE',
      revealed: false
    });
  }

  const startY = Math.min(from.gridY, to.gridY);
  const endY = Math.max(from.gridY, to.gridY);
  for (let y = startY; y <= endY; y++) {
    segments.push({
      id: `seg-v-${from.id}-${to.id}-${y}`,
      gridX: to.gridX,
      gridY: y,
      encounter: rng.next() > 0.9 ? 'TRAP' : 'NONE',
      revealed: false
    });
  }

  // Deduplicação por coordenadas para evitar sobreposição interna (quinas)
  const uniqueSegments = segments.reduce((acc: CorridorSegment[], current) => {
    const isDuplicate = acc.some(s => s.gridX === current.gridX && s.gridY === current.gridY);
    if (!isDuplicate) acc.push(current);
    return acc;
  }, []);

  return {
    fromId: from.id,
    toId: to.id,
    segments: uniqueSegments.filter(s => {
      return !allRooms.some(r => {
        const dx = s.gridX - r.gridX;
        const dy = s.gridY - r.gridY;
        const distSq = dx * dx + dy * dy;
        return distSq < 0.1;
      });
    })
  };
}
